import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import config from '../../config';

const STORAGE_KEY = 'permissionCodes';
const EVENT = 'hoteldesk-rbac-updated';

const PermissionContext = createContext(null);

function normalizePermissionEntry(entry) {
  if (entry == null) return '';
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'object') {
    return String(
      entry.code ?? entry.Code ?? entry.permissionCode ?? '',
    ).trim();
  }
  return '';
}

function readCodesFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizePermissionEntry).filter(Boolean);
  } catch {
    return [];
  }
}

function normalizePermissionList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizePermissionEntry).filter(Boolean);
}

export function notifyRbacUpdated() {
  window.dispatchEvent(new Event(EVENT));
}

export function PermissionProvider({ children }) {
  const [codes, setCodes] = useState(() => readCodesFromStorage());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const fromStorage = readCodesFromStorage();
    setCodes(fromStorage);

    const token = sessionStorage.getItem('token');
    const userId = sessionStorage.getItem('userId');
    if (!token || !userId) return;

    try {
      setLoading(true);
      const res = await fetch(
        `${config.API_BASE_URL}/api/rbac/me/permissions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.ok) {
        const data = await res.json();
        const list = data.permissionCodes ?? data.PermissionCodes ?? [];
        if (Array.isArray(list)) {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
          setCodes(normalizePermissionList(list));
        }
      }
    } catch {
      /* keep sessionStorage copy */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const on = () => {
      setCodes(readCodesFromStorage());
    };
    window.addEventListener(EVENT, on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener(EVENT, on);
      window.removeEventListener('storage', on);
    };
  }, []);

  const useGranularRbac = codes.length > 0;

  const hasPermission = useCallback(
    (code) => {
      if (!code) return true;
      if (!useGranularRbac) return true;
      const c = String(code).toUpperCase();
      return codes.some((x) => x.toUpperCase() === c);
    },
    [codes, useGranularRbac],
  );

  const hasAnyPermission = useCallback(
    (list) => {
      if (!list?.length) return true;
      return list.some((c) => hasPermission(c));
    },
    [hasPermission],
  );

  const value = useMemo(
    () => ({
      hasPermission,
      hasAnyPermission,
      permissionCodes: codes,
      useGranularRbac,
      loading,
      refresh,
    }),
    [hasPermission, hasAnyPermission, codes, useGranularRbac, loading, refresh],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

const LEGACY_PERMISSION_FALLBACK = {
  hasPermission: () => true,
  hasAnyPermission: () => true,
  permissionCodes: [],
  useGranularRbac: false,
  loading: false,
  refresh: () => {},
};

export function usePermission() {
  const ctx = useContext(PermissionContext);
  return ctx ?? LEGACY_PERMISSION_FALLBACK;
}

/** @deprecated use usePermission */
export function useRbacPermissions() {
  return usePermission();
}
